import { plural } from "@amber/contracts";
import { AppLink, Button, PopoverHeading, cx, formatCount } from "@amber/ui";
import type { HomeFrame, HomeResponse } from "@amber/application/contracts";
import { FRAME_SHORT, FRAME_TEXT, hoursText } from "./format";
import { Count, Panel, Pop, Tile } from "./parts";

type Delivery = NonNullable<HomeResponse["delivery"]>;

function metText(met: boolean | null): string {
  if (met === null) return "";
  return met ? "met" : "missed";
}

function Trend({ daily, label }: { daily: { count: number; in_frame: boolean }[]; label: string }) {
  const max = Math.max(1, ...daily.map((d) => d.count));
  const w = 92;
  const h = 30;
  const step = w / Math.max(1, daily.length - 1);
  const points = daily
    .map((d, i) => `${(i * step).toFixed(1)},${(h - 2 - (d.count / max) * (h - 4)).toFixed(1)}`)
    .join(" ");
  const first = daily.findIndex((d) => d.in_frame);
  return (
    <svg
      role="img"
      aria-label={label}
      width={w}
      height={h}
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      className="flex-none"
    >
      {first >= 0 ? (
        <rect x={first * step} y={0} width={w - first * step} height={h} className="fill-accent-soft" />
      ) : null}
      <polyline points={points} fill="none" strokeWidth={1.5} className="stroke-link" />
    </svg>
  );
}

function EligibleTile({ d, frame, team, big }: { d: Delivery; frame: HomeFrame; team: boolean; big: string }) {
  const change = d.eligible.count - d.eligible.previous;
  return (
    <Tile
      label={
        <Pop label="Eligible" team={team}>
          <PopoverHeading>Eligible communications</PopoverHeading>
          <p>
            Communications found in the time frame in which at least one tracked person's contributions satisfy the
            evidence-use policy.
          </p>
          <dl>
            <dt>Policy</dt>
            <dd>
              {d.eligible.policy
                ? `${d.eligible.policy.name}${d.eligible.policy.provisional ? " · provisional" : ""}`
                : "—"}
            </dd>
            <dt>Eligible</dt>
            <dd>
              {formatCount(d.eligible.count)} of {formatCount(d.eligible.finished)} finished
            </dd>
            <dt>Still Draft</dt>
            <dd>{formatCount(d.eligible.draft)}</dd>
            <dt>Previous period</dt>
            <dd>{formatCount(d.eligible.previous)}</dd>
          </dl>
          <p className="mt-2 text-muted">
            The latest days are still being processed. Rules in{" "}
            <AppLink href="/settings/checks" className="text-link">
              Settings › Checks and policies
            </AppLink>
            .
          </p>
        </Pop>
      }
    >
      <div className="flex items-end gap-2">
        <span className={cx("font-mono font-semibold leading-none", big)}>
          <Count href={d.eligible.href} count={d.eligible.count} team={team} label="Eligible communications" />
        </span>
        <Trend
          daily={d.eligible.daily}
          label={`Eligible communications per day over 30 days; ${formatCount(d.eligible.count)} in the ${FRAME_SHORT[frame]} highlighted`}
        />
      </div>
      <div className="mt-1 text-md text-muted">communications</div>
      <div className="mt-1 text-md text-muted">
        {change >= 0 ? "+" : "−"}
        {formatCount(Math.abs(change))} vs previous {FRAME_SHORT[frame]}
      </div>
      <div className="text-md text-muted">{formatCount(d.eligible.finished)} finished, incl. Draft</div>
    </Tile>
  );
}

function OnTimePopover({ d, team }: { d: Delivery; team: boolean }) {
  return (
    <Pop label="On time" team={team}>
      <PopoverHeading>Time from discovery, p95</PopoverHeading>
      <table className="w-full text-md">
        <thead className="text-muted">
          <tr>
            <th className="text-left font-normal">Stage</th>
            <th className="text-right font-normal">In stage</th>
            <th className="text-right font-normal">From discovery</th>
            <th className="text-right font-normal">Target</th>
          </tr>
        </thead>
        <tbody>
          {d.on_time.stages.map((s) => (
            <tr key={s.key}>
              <td>{s.label}</td>
              <td className="text-right">{hoursText(s.in_stage_hours)}</td>
              <td className="text-right">{hoursText(s.from_discovery_hours)}</td>
              <td className={cx("text-right", s.met === false && "text-attention")}>
                {hoursText(s.target_hours)} {metText(s.met)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.on_time.paused_note ? <p className="mt-2 text-waiting">{d.on_time.paused_note}</p> : null}
      {d.on_time.past_target.items.length > 0 ? (
        <>
          <p className="mt-2 font-semibold">Past target now</p>
          {d.on_time.past_target.items.map((i) => (
            <p key={i.id} className="text-md">
              {i.title} · {i.stage} · {hoursText(i.age_hours)} (target {hoursText(i.target_hours)})
              {i.people ? ` · ${i.people}` : ""}
            </p>
          ))}
        </>
      ) : null}
      <AppLink href={d.on_time.past_target.href} className="text-link">
        Overdue items in the backlog →
      </AppLink>
    </Pop>
  );
}

function OnTimeTile({ d, team, big }: { d: Delivery; team: boolean; big: string }) {
  return (
    <Tile label={<OnTimePopover d={d} team={team} />}>
      {d.on_time.enough ? (
        <span className={cx("font-mono font-semibold leading-none", big)}>{hoursText(d.on_time.p95_hours)}</span>
      ) : (
        <span className="text-xl font-semibold text-muted">Not enough data yet</span>
      )}
      <div className="mt-1 text-md text-muted">p95, found to eligible · target {hoursText(d.on_time.target_hours)}</div>
      {!d.on_time.enough ? (
        <div className="text-md text-muted">
          {plural(d.on_time.sample, "communication", { format: formatCount })} so far; needs 20
        </div>
      ) : null}
      {d.on_time.past_target.count > 0 ? (
        <div className="mt-1 text-md text-waiting">
          <Count
            href={d.on_time.past_target.href}
            count={d.on_time.past_target.count}
            team={team}
            label="Past target now"
            inline
          />{" "}
          past target now · oldest {hoursText(d.on_time.past_target.oldest_hours)}
        </div>
      ) : null}
    </Tile>
  );
}

function SourcesTile({ d, team, big }: { d: Delivery; team: boolean; big: string }) {
  return (
    <Tile
      label={
        <Pop label="Sources" team={team}>
          <PopoverHeading>Sources delivering</PopoverHeading>
          <p>
            Active monitored sources. Failing, partial and unusually quiet sources are not delivering; late sources
            count as delivering; paused sources are not counted.
          </p>
          {d.sources.problems.map((s) => (
            <p key={s.id} className="text-md">
              <b className="font-strong">{s.title}</b> {s.people ? `· ${s.people}` : ""} · {s.state}
              {s.reason ? ` · ${s.reason}` : ""}
            </p>
          ))}
          {d.sources.late > 0 ? (
            <p className="text-md text-muted">
              {plural(d.sources.late, "late source", { format: formatCount })} counted as delivering.
            </p>
          ) : null}
          <AppLink href={d.sources.not_delivering.href} className="text-link">
            Sources with problems →
          </AppLink>
        </Pop>
      }
    >
      <span className={cx("font-mono font-semibold leading-none", big)}>{formatCount(d.sources.delivering)}</span>
      <span className="ml-1.5 font-mono text-2xl text-muted">of {formatCount(d.sources.active)}</span>
      <div className="mt-1 text-md text-muted">delivering</div>
      <div className="mt-1 text-md text-muted">
        <Count
          href={d.sources.not_delivering.href}
          count={d.sources.not_delivering.count}
          team={team}
          label="Sources not delivering"
          inline
        />{" "}
        not delivering
        {d.sources.paused > 0 ? ` · ${formatCount(d.sources.paused)} paused, not counted` : ""}
      </div>
    </Tile>
  );
}

function Flow({
  d,
  frameText,
  team,
  onFunnel,
}: {
  d: Delivery;
  frameText: string;
  team: boolean;
  onFunnel: () => void;
}) {
  const maxFlow = Math.max(1, ...d.flow.steps.map((s) => s.count));
  return (
    <div className="mt-3 border-t border-divider pt-2.5">
      <div className="flex items-baseline gap-2">
        <h3 className="m-0 text-md font-semibold">Flow</h3>
        <span className="text-md text-muted">
          communications found in the {frameText.toLowerCase()}, and how far they got
        </span>
        {team ? null : (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={onFunnel}>
            Coverage funnel
          </Button>
        )}
      </div>
      <ol
        role="list"
        aria-label="Flow"
        className="m-0 mt-2 grid list-none grid-cols-4 gap-3 p-0 max-narrow:grid-cols-2"
      >
        {d.flow.steps.map((s) => (
          <li key={s.key}>
            <div className="text-md text-muted">{s.label}</div>
            <div className="font-mono text-xl font-semibold">
              <Count href={s.href} count={s.count} team={team} label={s.label} />
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-surface-2">
              <div
                className={cx("h-1.5 rounded-full", s.key === "eligible" ? "bg-link" : "bg-border/50")}
                style={{ width: `${String((s.count / maxFlow) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ol>
      <p className="m-0 mt-2 text-md text-muted">
        Not through:{" "}
        {d.flow.not_through.map((x) => (
          <span key={x.key} className="mr-3">
            <b className="font-strong text-fg">
              <Count href={x.href} count={x.count} team={team} label={`Not through, ${x.label}`} />
            </b>{" "}
            {x.label}
          </span>
        ))}
      </p>
    </div>
  );
}

export function DeliveryPanel({ data, team, onFunnel }: { data: HomeResponse; team: boolean; onFunnel: () => void }) {
  const d = data.delivery;
  if (!d) return null;
  const frameText = FRAME_TEXT[data.frame];
  const big = team ? "text-[52px]" : "text-[34px]";
  const frameDays = d.eligible.daily.filter((x) => x.in_frame).length;
  return (
    <Panel
      labelledBy="home-delivery"
      title="Delivery"
      lead={`${frameText} · communications, not copies`}
      link={team ? null : { href: d.eligible.href.replace(/[?&]readiness=eligible/, ""), label: "Backlog" }}
    >
      <div className="grid grid-cols-3 gap-0 max-narrow:grid-cols-1">
        <EligibleTile d={d} frame={data.frame} team={team} big={big} />
        <OnTimeTile d={d} team={team} big={big} />
        <SourcesTile d={d} team={team} big={big} />
      </div>
      <Flow d={d} frameText={frameText} team={team} onFunnel={onFunnel} />
      <p className="sr-only">{`${formatCount(frameDays)} days in the time frame.`}</p>
    </Panel>
  );
}
